import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Sprout, TreePine, Leaf, Trees } from "lucide-react";

/**
 * MissionSection — combined product + green mission.
 *
 * The pledge: 1 tree per AI agent, backfilled the moment we hit 1,000 live
 * agents (so we plant the first 1,000 trees in one go for our founding
 * cohort, then continue 1-for-1 as new agents publish).
 */

const MILESTONE = 1000;

export default function MissionSection() {
  const [agents, setAgents] = useState(100);

  useEffect(() => {
    const fetchStats = () =>
      api
        .get("/stats")
        .then((r) => setAgents(r.data.active_agents || 0))
        .catch(() => {});
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const progress = Math.min(100, (agents / MILESTONE) * 100);
  const milestoneReached = agents >= MILESTONE;
  const treesPledged = agents;
  const treesPlanted = milestoneReached ? agents : 0;
  const remaining = Math.max(0, MILESTONE - agents);

  const animatedAgents = useCountUp(agents);
  const animatedPledged = useCountUp(treesPledged);
  const animatedPlanted = useCountUp(treesPlanted);

  return (
    <section
      data-testid="mission-section"
      className="relative border-y border-emerald-100 bg-gradient-to-b from-emerald-50/50 via-white to-white overflow-hidden"
    >
      {/* Ambient background glow */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-20 w-[420px] h-[420px] rounded-full bg-emerald-300/15 blur-3xl"
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -left-24 w-[380px] h-[380px] rounded-full bg-teal-300/15 blur-3xl"
      />

      <div className="relative mx-auto max-w-7xl px-5 md:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-10 lg:gap-14 items-center">
        {/* Left column — copy + tracker */}
        <div>
          <p className="text-[10px] font-mono font-bold uppercase tracking-[0.24em] text-emerald-700 inline-flex items-center gap-2">
            <Leaf size={12} className="text-emerald-600" strokeWidth={2.4} />
            Our mission
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 mt-3 leading-[0.95]">
            Financial freedom.
            <br />
            <span className="text-emerald-600">One tree per agent.</span>
          </h2>
          <p className="text-slate-600 mt-5 text-[15px] leading-relaxed max-w-lg">
            Freelancity is built for the people the corporate machine wears down — hit by
            layoffs, drained by 60-hour weeks, stuck under toxic bosses, or watching AI quietly
            automate their roles.{" "}
            <span className="font-bold text-slate-900">
              Our mission is to help you stop trading your evenings, weekends and mental health
              for one paycheck
            </span>{" "}
            — upload your resume, let our AI turn your skills into a freelance business that
            earns in ₹ on autopilot, and finally make the money your experience deserves.
          </p>
          <p className="text-slate-600 mt-4 text-[15px] leading-relaxed max-w-lg">
            <span className="font-bold text-slate-900">
              And we pledge one real tree for every AI agent that goes live.
            </span>
          </p>

          {/* Milestone tracker card */}
          <div
            data-testid="milestone-tracker"
            className="mt-8 rounded-2xl border border-emerald-200 bg-white/90 backdrop-blur px-5 py-5 shadow-[0_18px_40px_-24px_rgba(16,185,129,0.35)]"
          >
            <div className="flex items-baseline justify-between gap-4">
              <div>
                <p
                  data-testid="milestone-agents"
                  className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900 tabular-nums leading-none"
                >
                  {animatedAgents.toLocaleString("en-IN")}
                  <span className="text-slate-400 font-bold text-xl sm:text-2xl">
                    {" "}
                    / {MILESTONE.toLocaleString("en-IN")}
                  </span>
                </p>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mt-1.5">
                  Live agents · milestone tracker
                </p>
              </div>
              <div className="text-right">
                <p
                  data-testid="trees-planted-count"
                  className="font-display text-3xl sm:text-4xl font-black tracking-tight text-emerald-600 tabular-nums leading-none inline-flex items-center gap-1.5"
                >
                  <TreePine size={22} strokeWidth={2.4} />
                  {(milestoneReached ? animatedPlanted : animatedPledged).toLocaleString("en-IN")}
                </p>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 mt-1.5">
                  {milestoneReached ? "Trees planted" : "Trees pledged"}
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-5">
              <div className="relative h-2.5 rounded-full bg-emerald-50 overflow-hidden ring-1 ring-emerald-100">
                <div
                  data-testid="milestone-progress-fill"
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 rounded-full transition-[width] duration-1000 ease-out"
                  style={{ width: `${progress}%` }}
                />
                {[25, 50, 75].map((t) => (
                  <span
                    key={t}
                    className="absolute top-0 bottom-0 w-px bg-white/70"
                    style={{ left: `${t}%` }}
                  />
                ))}
                <span
                  className={`absolute right-0.5 top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ring-2 ring-white shadow-md ${
                    milestoneReached
                      ? "bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.25)]"
                      : "bg-slate-300"
                  }`}
                />
              </div>
              <div className="mt-3 flex items-center justify-between gap-3 text-[11px]">
                {milestoneReached ? (
                  <span className="text-emerald-700 font-bold inline-flex items-center gap-1.5">
                    <Sprout size={12} strokeWidth={2.5} className="text-emerald-600" />
                    1,000-tree pledge honoured — every new agent plants another
                  </span>
                ) : (
                  <span className="text-slate-600">
                    <span className="font-bold text-slate-900 tabular-nums">
                      {remaining.toLocaleString("en-IN")}
                    </span>{" "}
                    more agents until we plant the first 1,000 trees
                  </span>
                )}
                <span className="text-slate-400 font-mono tabular-nums">
                  {progress.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>

          {/* Value props */}
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[12px]">
            <MissionPoint icon={Leaf} title="Real trees" text="Verified planting partner" />
            <MissionPoint
              icon={Sprout}
              title="Zero cost to you"
              text="Funded from our platform fee"
            />
            <MissionPoint icon={Trees} title="1:1 forever" text="One agent = one tree" />
          </ul>
        </div>

        {/* Right column — growing tree animation */}
        <div className="relative order-first md:order-none">
          <TreeArt
            milestoneReached={milestoneReached}
            treesPledged={treesPledged}
            treesPlanted={treesPlanted}
          />
        </div>
      </div>
    </section>
  );
}

/* -------- tree growth animation (Nano Banana frame sequence) -------- */

const STAGE_IMAGES = [
  "/tree-stages/stage1.png",
  "/tree-stages/stage2.png",
  "/tree-stages/stage3.png",
  "/tree-stages/stage4.png",
  "/tree-stages/stage5.png",
];
// Hold time on each stage before crossfading to the next
const STAGE_HOLD_MS = [1000, 950, 950, 1050];
const CROSSFADE_MS = 750;

function TreeArt({ milestoneReached, treesPledged, treesPlanted }) {
  const badgeCount = milestoneReached ? treesPlanted : treesPledged;
  const badgeLabel = milestoneReached ? "trees planted" : "trees pledged";

  const containerRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [runId, setRunId] = useState(0);
  const [stage, setStage] = useState(0);

  // Replay every time the art scrolls into view
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStage(0);
          setRunId((r) => r + 1);
          setInView(true);
        } else {
          setInView(false);
        }
      },
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Advance through growth stages while in view
  useEffect(() => {
    if (!inView || runId === 0) return;
    let cancelled = false;
    const timers = [];
    let acc = 0;
    for (let s = 1; s < STAGE_IMAGES.length; s++) {
      acc += STAGE_HOLD_MS[s - 1];
      timers.push(
        setTimeout(() => {
          if (!cancelled) setStage(s);
        }, acc)
      );
    }
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [inView, runId]);

  const grown = stage === STAGE_IMAGES.length - 1;

  return (
    <div
      ref={containerRef}
      data-testid="tree-art"
      className="relative h-[380px] sm:h-[440px] md:h-[500px] mx-auto max-w-md rounded-3xl overflow-hidden ring-1 ring-emerald-100 shadow-[0_24px_60px_-32px_rgba(16,185,129,0.4)]"
      aria-hidden="true"
    >
      {/* Frame stack — crossfaded growth stages */}
      {STAGE_IMAGES.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover select-none"
          style={{
            opacity: i === stage ? 1 : 0,
            transition: `opacity ${CROSSFADE_MS}ms ease-in-out`,
            animation:
              i === stage && grown && inView
                ? "treeBreathe 7s ease-in-out 1s infinite"
                : "none",
            transformOrigin: "center 80%",
          }}
        />
      ))}

      {/* Falling leaves — ambient, once the tree is grown */}
      {inView &&
        grown &&
        Array.from({ length: 5 }).map((_, i) => (
          <span
            key={`${runId}-leaf-${i}`}
            className="absolute text-emerald-600"
            style={{
              left: `${18 + i * 16}%`,
              top: `-8%`,
              animation: `leafFall ${9 + i * 1.5}s linear ${0.6 + i * 1.6}s infinite backwards`,
            }}
          >
            <Leaf size={14} strokeWidth={2.2} className="drop-shadow-sm" />
          </span>
        ))}

      {/* Stage dots */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
        {STAGE_IMAGES.map((_, i) => (
          <span
            key={i}
            className="rounded-full transition-all duration-500"
            style={{
              width: i === stage ? 16 : 6,
              height: 6,
              background: i <= stage ? "rgba(16,185,129,0.85)" : "rgba(16,185,129,0.25)",
            }}
          />
        ))}
      </div>

      {/* Trees badge */}
      {badgeCount > 0 && (
        <div
          data-testid="trees-planted-badge"
          className={`absolute top-4 left-4 rounded-full backdrop-blur border px-3 py-1.5 shadow-md inline-flex items-center gap-1.5 ${
            milestoneReached
              ? "bg-emerald-600 border-emerald-700 text-white"
              : "bg-white/95 border-emerald-200 text-slate-900"
          }`}
        >
          <Trees
            size={13}
            className={milestoneReached ? "text-white" : "text-emerald-600"}
            strokeWidth={2.5}
          />
          <span className="text-[11px] font-bold tabular-nums">
            {badgeCount.toLocaleString("en-IN")} {badgeLabel}
          </span>
        </div>
      )}

      <style>{`
        @keyframes leafFall {
          0% { transform: translate(0, 0) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          50% { transform: translate(-30px, 240px) rotate(220deg); }
          100% { transform: translate(20px, 480px) rotate(720deg); opacity: 0; }
        }
        @keyframes treeBreathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.025); }
        }
      `}</style>
    </div>
  );
}

/* -------- helpers -------- */

function MissionPoint({ icon: Icon, title, text }) {
  return (
    <li className="rounded-lg border border-emerald-100 bg-white/70 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-emerald-700">
        <Icon size={12} strokeWidth={2.5} />
        <span className="font-bold text-slate-900 text-[11px]">{title}</span>
      </div>
      <p className="text-slate-500 mt-0.5 text-[11px] leading-snug">{text}</p>
    </li>
  );
}

function useCountUp(target) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    cancelAnimationFrame(rafRef.current);
    const start = performance.now();
    const duration = 1200;
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target]);

  return value;
}
